import { TopicServiceInterface } from "./TopicServiceInterface";
import { Topic, Scenario, ContentStatus, } from "../../types/topics";
import { PgnParser } from "../../pgn/PgnParser";
export class TopicServiceLocal extends TopicServiceInterface {
    topics = new Map();
    scenarios = new Map();
    constructor() {
        super();
    }
    async getTopics() {
        return this.getPublicTopics();
    }
    async getTopicsByIds(topicIds) {
        const topics = topicIds
            .map((id) => this.topics.get(id))
            .filter((topic) => topic !== undefined);
        return this.sortTopics(topics);
    }
    async getPublicTopics() {
        return this.sortTopics(Array.from(this.topics.values()).filter((topic) => topic.isPublic));
    }
    async getUserTopics(userId) {
        return this.sortTopics(Array.from(this.topics.values()).filter((topic) => topic.ownerId === userId));
    }
    async getPublicAndUserTopics(userId) {
        return this.sortTopics(Array.from(this.topics.values()).filter((topic) => topic.isPublic || topic.ownerId === userId));
    }
    sortTopics(topics) {
        return topics.sort((a, b) => {
            // First compare by difficulty level
            if (a.difficultyLevel && b.difficultyLevel) {
                const difficultyComparison = a.difficultyLevel.localeCompare(b.difficultyLevel);
                if (difficultyComparison !== 0)
                    return difficultyComparison;
            }
            else if (a.difficultyLevel) {
                return -1;
            }
            else if (b.difficultyLevel) {
                return 1;
            }
            // If difficulty levels are equal or both missing, sort by title
            return a.title.localeCompare(b.title);
        });
    }
    async getScenarios(topicId) {
        return Array.from(this.scenarios.values())
            .filter((scenario) => scenario.topicId === topicId)
            .sort((a, b) => a.order - b.order);
    }
    async getAllScenarios(options) {
        const { page = 1, limit = 20, topicIds } = options || {};
        // Get all scenarios
        let allScenarios = [];
        if (topicIds && topicIds.length > 0) {
            // If specific topic IDs are provided, get scenarios only from those topics
            allScenarios = Array.from(this.scenarios.values()).filter((scenario) => topicIds.includes(scenario.topicId));
        }
        else {
            // Otherwise, get scenarios from all topics
            allScenarios = Array.from(this.scenarios.values());
        }
        // Sort scenarios by updatedAt (newest first)
        allScenarios.sort((a, b) => {
            if (a.updatedAt && b.updatedAt) {
                return b.updatedAt.getTime() - a.updatedAt.getTime();
            }
            return 0;
        });
        // Apply pagination
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        return allScenarios.slice(startIndex, endIndex);
    }
    async createTopic(topicData) {
        const newTopic = new Topic(topicData);
        this.topics.set(newTopic.id, newTopic);
        return newTopic;
    }
    async updateTopic(topicId, topicData) {
        const topic = this.topics.get(topicId);
        if (!topic) {
            throw new Error(`Topic with id ${topicId} not found`);
        }
        // Create a new data object without protected fields
        const allowedData = { ...topicData };
        delete allowedData.isPublic;
        delete allowedData.ownerId;
        delete allowedData.owner;
        delete allowedData.originalTopicId;
        topic.update(allowedData);
        this.topics.set(topicId, topic);
        return topic;
    }
    async createScenario(topicId, scenarioData) {
        if (!this.topics.has(topicId)) {
            throw new Error(`Topic with id ${topicId} not found`);
        }
        const existingScenarios = await this.getScenarios(topicId);
        const order = scenarioData.order ?? Scenario.getNextOrder(existingScenarios);
        const newScenario = new Scenario(topicId, {
            ...scenarioData,
            order,
            createdAt: new Date(),
            updatedAt: new Date(),
        });
        this.scenarios.set(newScenario.id, newScenario);
        return newScenario;
    }
    async updateScenario(topicId, scenarioId, scenarioData) {
        const scenario = this.scenarios.get(scenarioId);
        if (!scenario || scenario.topicId !== topicId) {
            throw new Error(`Scenario with id ${scenarioId} not found in topic ${topicId}`);
        }
        scenarioData.updatedAt = new Date();
        scenario.update(scenarioData);
        this.scenarios.set(scenarioId, scenario);
        return scenario;
    }
    async reorderScenarios(topicId, fromIndex, toIndex) {
        const scenarios = await this.getScenarios(topicId);
        const reorderedScenarios = Scenario.reorder(scenarios, fromIndex, toIndex);
        // Update all scenarios with their new orders
        for (const scenario of reorderedScenarios) {
            this.scenarios.set(scenario.id, scenario);
        }
        return reorderedScenarios;
    }
    async importScenarios(topicId, pgnText) {
        if (!this.topics.has(topicId)) {
            throw new Error(`Topic with id ${topicId} not found`);
        }
        const existingScenarios = await this.getScenarios(topicId);
        const result = { created: [], existing: [] };
        const games = pgnText
            .split(PgnParser.REGEX.GAME_SPLITTER)
            .filter((game) => PgnParser.isValidPgn(game));
        for (const game of games) {
            const normalizedPgn = PgnParser.normalize(game);
            const existingScenario = existingScenarios.find((s) => PgnParser.normalize(s.pgn) === normalizedPgn);
            if (existingScenario) {
                result.existing.push(existingScenario);
                continue;
            }
            const now = new Date();
            const newScenario = new Scenario(topicId, {
                pgn: game,
                order: Scenario.getNextOrder(existingScenarios.concat(result.created)),
                createdAt: now,
                updatedAt: now,
            });
            this.scenarios.set(newScenario.id, newScenario);
            result.created.push(newScenario);
        }
        return result;
    }
    async cloneToPublic(topicId, userId, owner) {
        const originalTopic = this.topics.get(topicId);
        if (!originalTopic) {
            throw new Error(`Topic with id ${topicId} not found`);
        }
        // Create new public topic data
        const publicTopicData = {
            title: `${originalTopic.title} (from Private)`,
            description: originalTopic.description,
            difficultyLevel: originalTopic.difficultyLevel,
            status: ContentStatus.Draft,
            isPublic: true,
            ownerId: userId,
            owner: owner,
            tags: [...originalTopic.tags],
            urls: [...originalTopic.urls],
            workbookRef: originalTopic.workbookRef,
            originalTopicId: originalTopic.id,
        };
        // Create the public topic
        const publicTopic = await this.createTopic(publicTopicData);
        // Get all scenarios from original topic
        const scenarios = await this.getScenarios(topicId);
        // Create scenarios in the new topic
        if (scenarios.length > 0) {
            const pgnText = scenarios.map((s) => s.pgn).join("\n\n");
            await this.importScenarios(publicTopic.id, pgnText);
        }
        return publicTopic;
    }
}
